/**
 * Persistent ground decals (PRD §5.4).
 *
 * Scorch marks left behind by hits. Decals are flat alpha-blended
 * quads placed slightly above the floor (`y = 0.01`) so they sort
 * correctly above the hex floor. Persistent within a match — recomputed
 * from the event stream per PRD §3.3 (no disk persistence).
 *
 * Implementation note: the rendered decal is a unit-radius circle with
 * a smooth alpha falloff. P5 swaps this for authored decal textures
 * (data-only change — `decals/` becomes a directory of PNGs).
 */

import { useMemo } from "react";
import { CanvasTexture } from "three";

export interface DecalSpec {
  /** Stable per-decal id. */
  id: number | string;
  /** World-space center. Y is ignored — decals stamp on the floor plane. */
  origin: [number, number, number];
  /** Decal radius in world units. */
  radius: number;
  /** Strength of the burn (alpha 0..1). */
  opacity: number;
  /** Tint color — typical scorch is "#0d0d0d", energy hits read brighter. */
  color: string;
}

interface DecalsProps {
  decals: ReadonlyArray<DecalSpec>;
  /** Hard cap on persistent decals (oldest dropped first). */
  max?: number;
}

export function Decals({ decals, max = 200 }: DecalsProps) {
  const texture = useMemo(() => makeScorchTexture(), []);
  const list = decals.length > max ? decals.slice(-max) : decals;

  return (
    <group name="decals">
      {list.map((d) => (
        <mesh
          key={d.id}
          position={[d.origin[0], 0.01, d.origin[2]]}
          rotation={[-Math.PI / 2, 0, 0]}
          renderOrder={1}
          receiveShadow={false}
        >
          <circleGeometry args={[d.radius, 32]} />
          <meshBasicMaterial
            map={texture}
            color={d.color}
            transparent
            opacity={d.opacity}
            depthWrite={false}
          />
        </mesh>
      ))}
    </group>
  );
}

/**
 * Procedural radial scorch texture — a single soft black circle on a
 * transparent background. Cheap; one CPU-side canvas read per session.
 */
function makeScorchTexture(): CanvasTexture {
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, "rgba(0,0,0,1)");
  grad.addColorStop(0.55, "rgba(0,0,0,0.35)");
  grad.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  return new CanvasTexture(canvas);
}
